// ============================================================
// avaliacaoFisica.js — o cálculo da avaliação física e antropométrica
//
// O programa coleta dobras cutâneas, perímetros e diâmetros em campo e
// até aqui compilava esses números no Physical Test 8.0, fora do sistema.
// Isso custa duas coisas: a série histórica do atleta fica presa num
// arquivo do computador de quem mediu, e o número que chega ao relatório
// não tem como ser conferido contra a medida que o gerou.
//
// Este arquivo é só a conta. Não lê Firestore, não desenha tela e não
// formata PDF, pelo mesmo motivo de sempre: equação de composição
// corporal errada dentro de um componente React só aparece quando alguém
// olha o número na tela e desconfia. Aqui ela aparece no teste.
//
// ── Os dois protocolos ──────────────────────────────────────
//
// O programa usa dois, e só dois:
//
//   1. Deurenberg (1990), 4 dobras, 7 a 17 anos, masculino e feminino —
//      percentual de gordura.
//   2. Somatotipo de Heath-Carter — endomorfia, mesomorfia, ectomorfia.
//
// A escolha não é técnica, é de continuidade. O perfil ISAK completo é
// mais rico, mas depende de avaliador acreditado, e o avaliador que fez
// o perfil de 2024 não segue para as reavaliações. Protocolo que só uma
// pessoa pode repetir não produz série histórica: produz um ponto solto.
// Deurenberg e Heath-Carter a própria equipe mede e remede.
//
// ── Por que o ISAK antigo ainda entra ───────────────────────
//
// O perfil ISAK completo contém todos os sítios que os dois protocolos
// pedem: bíceps, tríceps, subescapular e crista ilíaca (Deurenberg);
// tríceps, subescapular, supraespinhal e panturrilha, mais estatura,
// massa, braço fletido, perímetro da perna e os diâmetros de úmero e
// fêmur (Heath-Carter). Então a avaliação ISAK de 2024 não é descartada:
// `medidasDeISAK` traduz os nomes dos sítios e a avaliação vira o
// primeiro ponto da série, calculada pelos mesmos dois protocolos que
// calculam os pontos seguintes. Comparar 2024 com 2026 passa a ser
// comparar duas contas iguais, não duas contas diferentes.
//
// ── Referências ─────────────────────────────────────────────
//
// Deurenberg P, Pieters JJL, Hautvast JGAJ (1990). The assessment of the
//   body fat percentage by skinfold thickness measurements in childhood
//   and young adolescence. Br J Nutr 63:293-303.
// Weststrate JA, Deurenberg P (1989). Body composition in children:
//   proposal for a method for calculating body fat percentage from total
//   body density or skinfold-thickness measurements. Am J Clin Nutr
//   50:1104-15.
// Carter JEL, Heath BH (1990). Somatotyping: development and
//   applications. Cambridge University Press.
// Mirwald RL, Baxter-Jones ADG, Bailey DA, Beunen GP (2002). An
//   assessment of maturity from anthropometric measurements. Med Sci
//   Sports Exerc 34:689-94.
// Moore SA, McKay HA, Macdonald H, et al. (2015). Enhancing a somatic
//   maturity prediction model. Med Sci Sports Exerc 47:1755-64.
// ============================================================

import { idadeDecimalEm } from "./idade";

// ── Vocabulário das medidas ──────────────────────────────────
//
// Os nomes dos sítios ficam aqui e não espalhados pelas telas porque o
// mesmo sítio aparece com nomes diferentes na literatura e nos informes:
// a "supra-ilíaca" de Durnin-Womersley é a "crista ilíaca" do ISAK, e
// nenhuma das duas é a "supraespinhal", que é outro ponto do corpo.
// Misturar as duas troca o protocolo sem ninguém perceber.
//
// ── Por que a bateria é completa, e não só o que o protocolo pede ──
//
// Decisão de 14/10/2026, do Diego: a coleta passa a ser a bateria
// inteira, no formato do perfil ISAK, em vez de só os sítios do
// protocolo do dia.
//
// A razão é prática e já custou caro uma vez. As 76 coletas de 2026
// foram feitas com os sítios de um protocolo, e quando a avaliação
// precisou de outro os dados não existiam mais: não dá para voltar a
// medir uma dobra de março. Medir tudo custa uns minutos a mais com o
// atleta na frente e deixa QUALQUER protocolo disponível depois, para
// trás inclusive, porque o resultado é recalculado na leitura.
//
// O que o protocolo escolhido faz é MARCAR na tela o que ele precisa.
// Nada fica obrigatório: coleta parcial grava, e o que não tem medida
// não produz resultado em vez de produzir resultado errado.
//
// ── O lado do corpo ─────────────────────────────────────────
//
// Dobras e diâmetros: um valor, no lado DIREITO, que é a convenção do
// ISAK e o que as equações pedem.
//
// Perímetros de membro: os dois lados, desde 14/10/2026, a pedido do
// Diego. Braço, antebraço, coxa e panturrilha são medidos dos dois lados
// na coleta e o relatório do Physical Test já imprime assim. O campo sem
// sufixo é o DIREITO, que é o que entra no somatotipo e nas contas, e o
// esquerdo entra num campo irmão terminado em `Esq`. Manter o direito no
// nome antigo é o que deixa as 76 coletas de 2026 continuarem válidas
// sem conversão.

export const DOBRAS = [
  { id: "triceps", rotulo: "Tríceps", isak: "Tríceps" },
  { id: "subescapular", rotulo: "Subescapular", isak: "Subescapular" },
  { id: "biceps", rotulo: "Bíceps", isak: "Bíceps" },
  { id: "supraIliaca", rotulo: "Supra-ilíaca", isak: "Crista ilíaca" },
  { id: "supraEspinhal", rotulo: "Supraespinhal", isak: "Supraspinal" },
  { id: "abdominal", rotulo: "Abdominal", isak: "Abdominal" },
  { id: "coxa", rotulo: "Coxa", isak: "Coxa" },
  { id: "panturrilha", rotulo: "Panturrilha medial", isak: "Perna" },
  // Peitoral e axilar média não existem no perfil ISAK e existem nos
  // protocolos de adulto: peitoral em Pollock masculino, axilar média em
  // Pollock de 7 dobras e nas equações brasileiras de 9 dobras.
  { id: "peitoral", rotulo: "Peitoral", isak: null },
  { id: "axilarMedia", rotulo: "Axilar média", isak: null },
];

export const PERIMETROS = [
  { id: "pescoco", rotulo: "Pescoço", isak: "Pescoço" },
  { id: "torax", rotulo: "Tórax", isak: "Tórax" },
  { id: "cintura", rotulo: "Cintura", isak: "Cintura" },
  { id: "abdome", rotulo: "Abdome", isak: null },
  { id: "quadril", rotulo: "Quadril", isak: "Quadril" },
  { id: "bracoRelaxado", rotulo: "Braço relaxado", isak: "Braço relaxado", esquerdo: "bracoRelaxadoEsq" },
  {
    id: "bracoContraido", rotulo: "Braço fletido e contraído",
    isak: "Braço fletido e contraído", esquerdo: "bracoContraidoEsq",
  },
  { id: "antebraco", rotulo: "Antebraço", isak: "Antebraço", esquerdo: "antebracoEsq" },
  { id: "punho", rotulo: "Punho", isak: "Punho" },
  { id: "coxa", rotulo: "Coxa", isak: "Coxa (1 cm abaixo do glúteo)", esquerdo: "coxaEsq" },
  { id: "panturrilha", rotulo: "Panturrilha", isak: "Perna", esquerdo: "panturrilhaEsq" },
  { id: "tornozelo", rotulo: "Tornozelo", isak: "Tornozelo" },
];

// A mesma lista achatada, com uma entrada por CAMPO: o sítio de membro
// vira dois, direito e esquerdo, nessa ordem.
//
// Existe porque a tela lança e a tabela imprime campo a campo, enquanto
// as equações pedem o sítio, e as duas leituras não cabem na mesma
// lista. `espelho` aponta de volta para o lado direito, que é o que o
// cálculo usa.
export const PERIMETROS_POR_CAMPO = PERIMETROS.flatMap(p => (
  p.esquerdo
    ? [
      { ...p, rotulo: `${p.rotulo} (D)` },
      { id: p.esquerdo, rotulo: `${p.rotulo} (E)`, isak: p.isak, espelho: p.id },
    ]
    : [p]
));

export const DIAMETROS = [
  { id: "biacromial", rotulo: "Biacromial", isak: "Biacromial" },
  { id: "toraxTransverso", rotulo: "Tórax transverso", isak: "Tórax transverso" },
  { id: "toraxAnteroPosterior", rotulo: "Tórax ântero-posterior", isak: "Tórax ântero-posterior" },
  { id: "cristaIliaca", rotulo: "Crista ilíaca (biileocristal)", isak: "Biileocristal" },
  { id: "bitrocanterica", rotulo: "Bitrocantérica", isak: null },
  { id: "umero", rotulo: "Biepicôndilo umeral", isak: "Úmero" },
  { id: "femur", rotulo: "Biepicôndilo femoral", isak: "Fêmur" },
  // Estes dois entram na massa óssea de Rocha e não entravam na coleta.
  // É por falta deles que as 76 coletas de 2026 não produzem
  // fracionamento: sem o punho não há peso ósseo, e sem peso ósseo não
  // há peso muscular.
  { id: "biestiloide", rotulo: "Biestiloide (punho)", isak: "Biestiloide" },
  { id: "bimaleolar", rotulo: "Bimaleolar (tornozelo)", isak: "Bimaleolar" },
];

// Alturas e comprimentos, em cm. A estatura em pé fica fora desta lista
// porque ela é medida básica e aparece junto do peso na tela.
export const ALTURAS = [
  { id: "estaturaSentado", rotulo: "Estatura sentado", isak: "Altura tronco-cefálica" },
  { id: "envergadura", rotulo: "Envergadura", isak: "Envergadura" },
  { id: "alturaTrocanterica", rotulo: "Altura trocantérica", isak: "Altura trocantérica" },
];

// As dobras que cada protocolo exige. Usado para dizer na tela o que
// ainda falta medir antes de o resultado sair, em vez de devolver um
// número calculado em cima de campo vazio.
export const DOBRAS_DEURENBERG = ["biceps", "triceps", "subescapular", "supraIliaca"];
export const DOBRAS_SOMATOTIPO = ["triceps", "subescapular", "supraEspinhal", "panturrilha"];

// Os protocolos de 3 dobras para adulto.
//
// Deurenberg foi derivado em criança e adolescente e para de valer aos 18.
// Daí em diante o programa usa um destes dois, e os sítios mudam com o
// sexo, o que é da natureza das equações e não escolha nossa.
//
// Jackson e Pollock é o padrão internacional e leva a idade como termo.
// Guedes foi derivado em universitários brasileiros de 18 a 30 anos, que é
// exatamente o perfil dos atletas mais velhos do programa, e não leva
// idade: é log do somatório e mais nada.
export const DOBRAS_POLLOCK = {
  masculino: ["peitoral", "abdominal", "coxa"],
  feminino: ["triceps", "supraIliaca", "coxa"],
};

export const DOBRAS_GUEDES = {
  masculino: ["triceps", "supraIliaca", "abdominal"],
  feminino: ["subescapular", "supraIliaca", "coxa"],
};

// A idade em que Deurenberg para e os de adulto começam.
export const IDADE_ADULTA = 18;

export const PROTOCOLOS_DE_GORDURA = ["deurenberg", "pollock", "guedes"];

// Qual protocolo vale para esta idade.
//
// Deurenberg cobre 7 a 17 anos e é o que o programa usa na imensa maioria
// dos atletas. A partir dos 18 ele sai de validade e entra o de adulto,
// que por padrão é Pollock.
//
// A escolha é automática de propósito: quem está medindo não deve ter que
// lembrar em que aniversário o protocolo troca, e atleta avaliado com a
// equação errada produz série que parece evolução e é troca de fórmula.
export function protocoloDaIdade(idade, preferidoAdulto = "pollock") {
  if (!Number.isFinite(idade)) return null;
  if (idade < IDADE_ADULTA) return "deurenberg";
  return preferidoAdulto === "guedes" ? "guedes" : "pollock";
}

export function dobrasDoProtocolo(protocolo, sexo) {
  if (protocolo === "deurenberg") return DOBRAS_DEURENBERG;
  if (protocolo === "pollock") return DOBRAS_POLLOCK[sexo] || [];
  if (protocolo === "guedes") return DOBRAS_GUEDES[sexo] || [];
  return [];
}

// ── Utilidades ───────────────────────────────────────────────

// Número medido, ou null.
//
// Zero é tratado como ausência de medida, e isso é deliberado: o
// Physical Test grava 0,00 em todo sítio que o avaliador não mediu, e os
// relatórios exportados chegam cheios desses zeros. Dobra cutânea de
// 0 mm não existe em ser humano vivo, então 0 aqui só pode significar
// "não medido". Somar esses zeros produziria um somatório menor e um
// percentual de gordura falsamente baixo, que é o pior tipo de erro
// porque parece um resultado bom.
function medida(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Soma de uma lista de sítios, ou null se faltar algum.
function somaDe(dobras, sitios) {
  let total = 0;
  for (const s of sitios) {
    const v = medida(dobras ? dobras[s] : null);
    if (v === null) return null;
    total += v;
  }
  return total;
}

export function somaDasDobras(dobras, sitios) {
  return somaDe(dobras, sitios);
}

// Soma do que FOI medido, ignorando o que falta.
//
// Diferente de `somaDe` de propósito. Para rodar um protocolo, faltar um
// sítio invalida a conta inteira e a soma tem de ser null. Para
// acompanhar a adiposidade de um atleta no tempo, a soma dos sítios que
// existem é a variável mais honesta que a antropometria tem: ela é
// medida, e o percentual é estimativa em cima dela. Devolve null quando
// não mediram nada.
export function somaDoQueFoiMedido(dobras, sitios) {
  let total = 0;
  let achou = false;
  for (const s of sitios) {
    const v = medida(dobras ? dobras[s] : null);
    if (v !== null) {
      total += v;
      achou = true;
    }
  }
  return achou ? total : null;
}

// Quais sítios faltam para rodar um protocolo.
export function dobrasFaltantes(dobras, sitios) {
  return sitios.filter(s => medida(dobras ? dobras[s] : null) === null);
}

function arred(n, casas = 2) {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  const f = Math.pow(10, casas);
  return Math.round(n * f) / f;
}

// ── Deurenberg: densidade e percentual de gordura ────────────

// Deurenberg publicou TRÊS pares de equações, um por estágio
// maturacional: pré-púbere, púbere e pós-púbere. A idade entra como termo
// nos dois primeiros e não entra no terceiro, e isso não é detalhe de
// forma: é o achado do artigo, que a idade prevê densidade enquanto o
// atleta ainda está crescendo e deixa de prever depois.
//
// ── O que estava errado aqui até 14/10/2026 ─────────────────
//
// O estágio púbere não existia, e o que o arquivo chamava de pós-púbere
// eram os coeficientes de Durnin e Womersley para menores de 17 anos
// (masculino 1,1533/0,0643 e feminino 1,1369/0,0598), que são de outro
// estudo e de outra amostra. A conta rodava e devolvia número plausível,
// que é como esse tipo de erro sobrevive.
//
// A conferência que pegou: o informe do Physical Test do Adrian Dastan de
// 26/08/2026, o único relatório de Deurenberg completo que o programa
// tem. Somatório de 29,50 mm, 13,67 anos, nível maturacional "Púbere",
// 15,58% impresso. Com a equação de púbere e a conversão de Weststrate
// saem 15,77%, contra 16,52% pelo que estava aqui antes. A diferença que
// sobra, de 0,19 ponto, é compatível com a idade que o programa usa: ele
// chega a 15,58% com 13,78 anos, e o nosso cadastro tem 25/12/2012.
//
// O estágio é escolhido pelo avaliador, como o Physical Test faz, e a
// tela sugere um a partir do pico de velocidade de crescimento. Usar a
// idade como divisor seria proxy grosseiro: aos 15 anos parte dos meninos
// ainda é pré-púbere e parte das meninas já passou do pico há dois anos.
const DEURENBERG = {
  masculino: {
    pre: { a: 1.1133, b: 0.0561, idade: 0.0017 },
    pubere: { a: 1.0555, b: 0.0352, idade: 0.0038 },
    pos: { a: 1.1324, b: 0.0429, idade: 0 },
  },
  feminino: {
    pre: { a: 1.1187, b: 0.063, idade: 0.0019 },
    pubere: { a: 1.1074, b: 0.0504, idade: 0.0016 },
    pos: { a: 1.183, b: 0.0813, idade: 0 },
  },
};

export const ESTAGIOS_MATURACIONAIS = [
  { id: "pre", rotulo: "Pré-púbere" },
  { id: "pubere", rotulo: "Púbere" },
  { id: "pos", rotulo: "Pós-púbere" },
];

export function rotuloDoEstagio(estagio) {
  const e = ESTAGIOS_MATURACIONAIS.find(x => x.id === estagio);
  return e ? e.rotulo : null;
}

// O estágio que o sistema sugere, a partir do pico de velocidade de
// crescimento que as equações de maturação estimam.
//
// O estágio de Deurenberg é de Tanner, que o programa não coleta e não
// vai coletar: é exame de puberdade em criança e adolescente, e não cabe
// num ginásio. O PVC é o melhor substituto que existe com fita e balança,
// e a correspondência é direta: faltando mais de um ano para o pico é
// pré-púbere, na janela de um ano para cada lado é púbere, passado mais
// de um ano é pós-púbere.
//
// Sem estatura não há PVC, e aí a sugestão cai na idade, com os limites
// das médias do próprio artigo: meninas púberes aos 13,1 e pós-púberes
// aos 16,8; meninos púberes aos 13,8 e pós-púberes aos 17,5. É proxy, a
// tela diz que é, e o avaliador corrige.
export const IDADE_DIVISORA_PUBERDADE = 15;

export function estagioSugerido({ sexo, idade, offsetPVC = null }) {
  if (Number.isFinite(offsetPVC)) {
    if (offsetPVC < -1) return "pre";
    if (offsetPVC <= 1) return "pubere";
    return "pos";
  }
  if (!Number.isFinite(idade)) return null;
  const limites = sexo === "feminino" ? [11, 15] : [12, 16];
  if (idade < limites[0]) return "pre";
  if (idade < limites[1]) return "pubere";
  return "pos";
}

// Densidade corporal em g/cm³ a partir do log do somatório de 4 dobras.
//
// `estagio` é o estágio maturacional, e é ele que escolhe a equação. Sem
// ele a função usa `estagioSugerido`, e o resultado diz qual foi usado e
// se foi sugerido ou informado: a diferença entre púbere e pós-púbere no
// mesmo somatório passa de 1 ponto de gordura, e um relatório que não
// mostra o estágio não dá para auditar.
export function densidadeDeurenberg({ sexo, idade, dobras, estagio = null, offsetPVC = null }) {
  const tabela = DEURENBERG[sexo];
  if (!tabela) return null;
  const soma = somaDe(dobras, DOBRAS_DEURENBERG);
  if (soma === null || !Number.isFinite(idade)) return null;

  const informado = tabela[estagio] ? estagio : null;
  const fase = informado || estagioSugerido({ sexo, idade, offsetPVC }) || "pre";
  const coef = tabela[fase];
  if (!coef) return null;

  return {
    densidade: coef.a - coef.b * Math.log10(soma) + coef.idade * idade,
    soma,
    fase,
    estagioInformado: Boolean(informado),
  };
}

// Da densidade para o percentual de gordura.
//
// Siri e Brozek foram derivadas em adultos e assumem uma massa magra com
// densidade fixa. Criança não tem: o osso mineraliza e a água corporal
// cai ao longo da infância, e aplicar a constante de adulto superestima
// a gordura de quem é mais novo, justamente a faixa que o programa mais
// atende. Weststrate e Deurenberg publicaram a versão que varia com
// idade e sexo, e é ela que fica como padrão — é também a que acompanha
// as equações de densidade acima no trabalho do próprio Deurenberg.
//
// Siri e Brozek continuam disponíveis porque relatório antigo e planilha
// de outro serviço vêm calculados nelas, e comparar duas séries exige
// poder recalcular a nossa na conversão da outra.
export const CONVERSOES = ["weststrate", "siri", "brozek"];

export function gorduraDaDensidade(densidade, { conversao = "weststrate", sexo, idade } = {}) {
  if (!Number.isFinite(densidade) || densidade <= 0) return null;

  if (conversao === "siri") return 495 / densidade - 450;
  if (conversao === "brozek") return 457 / densidade - 414.2;

  if (!Number.isFinite(idade)) return null;
  if (sexo === "feminino") {
    return (553 - 7.3 * (idade - 10)) / densidade - (514 - 8 * (idade - 10));
  }
  if (sexo === "masculino") {
    return (562 - 4.2 * (idade - 2)) / densidade - (525 - 4.7 * (idade - 2));
  }
  return null;
}

// Faixas de classificação do percentual de gordura em crianças e
// adolescentes (Lohman, 1987). São faixas de referência de saúde, não de
// desempenho: atleta de rendimento costuma ficar abaixo de "adequado"
// sem que isso seja achado clínico. A tela precisa dizer isso ao lado do
// rótulo, senão a faixa vira diagnóstico.
const FAIXAS_GORDURA = {
  masculino: [
    [6, "Muito baixo"],
    [11, "Baixo"],
    [21, "Adequado"],
    [26, "Moderadamente alto"],
    [32, "Alto"],
    [Infinity, "Muito alto"],
  ],
  feminino: [
    [12, "Muito baixo"],
    [16, "Baixo"],
    [26, "Adequado"],
    [31, "Moderadamente alto"],
    [37, "Alto"],
    [Infinity, "Muito alto"],
  ],
};

export function classificarGordura(percentual, sexo) {
  const faixas = FAIXAS_GORDURA[sexo];
  if (!faixas || !Number.isFinite(percentual)) return null;
  return faixas.find(([limite]) => percentual < limite)[1];
}

// O protocolo inteiro: densidade, percentual, massa gorda e massa magra.
export function deurenberg({
  sexo,
  idade,
  peso,
  dobras,
  estagio = null,
  offsetPVC = null,
  conversao = "weststrate",
}) {
  // O caminho acompanha o nome do sítio, igual ao que `somatotipo`
  // devolve, para a tela poder tratar as duas listas do mesmo jeito.
  const faltam = dobrasFaltantes(dobras, DOBRAS_DEURENBERG).map(id => `dobras.${id}`);
  if (faltam.length) return { completo: false, faltam, soma: null, percentualGordura: null };

  const d = densidadeDeurenberg({ sexo, idade, dobras, estagio, offsetPVC });
  if (!d) return { completo: false, faltam: [], soma: null, percentualGordura: null };

  const percentual = gorduraDaDensidade(d.densidade, { conversao, sexo, idade });
  if (percentual === null) {
    return { completo: false, faltam: [], soma: arred(d.soma, 1), percentualGordura: null };
  }

  const kg = medida(peso);
  const massaGorda = kg === null ? null : (kg * percentual) / 100;

  return {
    completo: true,
    faltam: [],
    soma: arred(d.soma, 1),
    // O estágio acompanha o resultado para sempre, com a marca de quem o
    // escolheu. Dois percentuais do mesmo atleta só se comparam se
    // tiverem saído do mesmo estágio.
    estagio: d.fase,
    estagioInformado: d.estagioInformado,
    conversao,
    densidade: arred(d.densidade, 4),
    percentualGordura: arred(percentual, 2),
    massaGorda: arred(massaGorda, 2),
    massaMagra: kg === null || massaGorda === null ? null : arred(kg - massaGorda, 2),
    classificacao: classificarGordura(percentual, sexo),
  };
}

// ── Gordura do adulto: Pollock e Guedes ──────────────────────
//
// Os dois estimam a densidade a partir de três dobras, e os sítios mudam
// com o sexo. Convertem por Siri, que é a conversão de adulto: a correção
// por idade de Weststrate existe porque criança tem massa magra menos
// densa, e aos 18 anos isso deixou de valer.
//
// Jackson AS, Pollock ML (1978). Generalized equations for predicting
//   body density of men. Br J Nutr 40:497-504.
// Jackson AS, Pollock ML, Ward A (1980). Generalized equations for
//   predicting body density of women. Med Sci Sports Exerc 12:175-82.
// Guedes DP (1985). Estudo da gordura corporal através da mensuração dos
//   valores de densidade corporal e da espessura de dobras cutâneas em
//   universitários. Kinesis 1(2):183-212.
const EQUACOES_DE_ADULTO = {
  pollock: {
    rotulo: "Jackson e Pollock (3 dobras)",
    // Quadrática no somatório, com termo de idade.
    masculino: { a: 1.10938, b: 0.0008267, c: 0.0000016, idade: 0.0002574 },
    feminino: { a: 1.0994921, b: 0.0009929, c: 0.0000023, idade: 0.0001392 },
  },
  guedes: {
    rotulo: "Guedes (3 dobras)",
    // Logarítmica, sem idade. Derivada em universitários brasileiros de 18
    // a 30 anos, que é o perfil dos atletas mais velhos do programa.
    masculino: { a: 1.17136, b: 0.06706 },
    feminino: { a: 1.1665, b: 0.07063 },
  },
};

export function rotuloDoProtocolo(protocolo) {
  if (protocolo === "deurenberg") return "Deurenberg (4 dobras)";
  const e = EQUACOES_DE_ADULTO[protocolo];
  return e ? e.rotulo : null;
}

// ── O protocolo escolhido à mão ──────────────────────────────
//
// A idade decide o protocolo sozinha, e isso continua sendo o padrão da
// tela. O que esta parte permite é o avaliador dizer outra coisa, porque
// existe uma escolha legítima: dos 18 em diante, Pollock e Guedes são os
// dois válidos, e a comissão pode preferir Guedes, que foi derivado em
// brasileiros da idade dos atletas mais velhos do programa.
//
// Fora dessa escolha, trocar de protocolo é erro, e o lugar de dizer isso
// é na tela de coleta, antes de a medida existir.

// A faixa de idade em que cada equação foi derivada. O limite de cima é
// aberto: `ate: 18` quer dizer "vale até 17 anos e 11 meses".
export const FAIXA_DO_PROTOCOLO = {
  deurenberg: { de: 7, ate: 18 },
  pollock: { de: 18, ate: 62 },
  guedes: { de: 18, ate: 31 },
};

// O protocolo serve para este atleta nesta data?
//
// Devolve `adequado: null` quando a pergunta não tem resposta, que é
// diferente de "não serve": sem sexo ou sem data de nascimento no
// cadastro não há faixa com que comparar, e o aviso tem que cobrar o
// cadastro em vez de culpar a escolha.
//
// `ressalva` é o meio do caminho: a equação roda, o número sai, e o
// atleta está fora da amostra em que ela foi derivada. Guedes acima dos
// 30 anos é o caso real. Recusar seria exagero e calar seria pior.
export function adequacaoDoProtocolo({ protocolo, sexo, idade }) {
  const base = {
    protocolo,
    sitios: dobrasDoProtocolo(protocolo, sexo),
    adequado: false,
    motivo: null,
    ressalva: null,
  };
  if (!PROTOCOLOS_DE_GORDURA.includes(protocolo)) {
    return { ...base, motivo: "Este protocolo não existe no sistema." };
  }

  const nome = rotuloDoProtocolo(protocolo);
  if (sexo !== "masculino" && sexo !== "feminino") {
    return {
      ...base,
      adequado: null,
      motivo:
        "O cadastro deste atleta não tem o sexo. As três equações têm coeficientes " +
        "próprios para cada um, e duas delas ainda medem dobras diferentes, então sem " +
        "esse campo não dá para dizer se o protocolo serve nem quais dobras pedir.",
    };
  }
  if (!Number.isFinite(idade)) {
    return {
      ...base,
      adequado: null,
      motivo:
        "O cadastro deste atleta não tem a data de nascimento, e a validade de cada " +
        "protocolo é uma faixa de idade.",
    };
  }

  const faixa = FAIXA_DO_PROTOCOLO[protocolo];
  const anos = `${idade.toFixed(1).replace(".", ",")} anos na data da coleta`;

  if (idade < faixa.de) {
    const alternativa = protocolo === "deurenberg"
      ? "Abaixo dos 7 anos nenhum dos três protocolos do sistema foi validado."
      : `Abaixo dos ${IDADE_ADULTA} anos o protocolo é Deurenberg.`;
    return {
      ...base,
      motivo: `${nome} foi derivado a partir dos ${faixa.de} anos, e o atleta tem ${anos}. ` +
        alternativa,
    };
  }

  // Deurenberg é o único limite rígido: aos 18 anos ele sai de validade e
  // existem dois protocolos de adulto para pôr no lugar. Nos de adulto, o
  // limite de cima da amostra é ressalva e não recusa, porque não há
  // terceira equação para oferecer.
  if (protocolo === "deurenberg" && idade >= faixa.ate) {
    return {
      ...base,
      motivo: `Deurenberg foi derivado em crianças e adolescentes de ${faixa.de} a ` +
        `${faixa.ate - 1} anos, e o atleta tem ${anos}. Dos ${IDADE_ADULTA} em diante o ` +
        "protocolo é Jackson e Pollock ou Guedes, de 3 dobras.",
    };
  }
  if (idade >= faixa.ate) {
    return {
      ...base,
      adequado: true,
      ressalva: `${nome} foi derivado em pessoas de ${faixa.de} a ${faixa.ate - 1} anos, ` +
        `e o atleta tem ${anos}. O resultado sai, e fica fora da faixa validada.`,
    };
  }

  return { ...base, adequado: true };
}

// As dobras que esta coleta precisa ter: as do protocolo de gordura mais
// as do somatotipo, sem repetir.
//
// Fica aqui e não na tela porque a ficha, o Excel e o PDF cobram a mesma
// lista, e duas listas iguais em dois arquivos divergem na primeira vez
// que alguém acrescenta um sítio em um só.
export function dobrasNecessarias(protocolo, sexo) {
  const doProtocolo = dobrasDoProtocolo(protocolo, sexo);
  if (!doProtocolo.length) return [...DOBRAS_SOMATOTIPO];
  return [...new Set([...doProtocolo, ...DOBRAS_SOMATOTIPO])];
}

// Densidade corporal pelos protocolos de adulto.
export function densidadeDeAdulto({ protocolo, sexo, idade, dobras }) {
  const eq = EQUACOES_DE_ADULTO[protocolo];
  const coef = eq && eq[sexo];
  if (!coef) return null;
  const sitios = dobrasDoProtocolo(protocolo, sexo);
  const soma = somaDe(dobras, sitios);
  if (soma === null) return null;

  if (protocolo === "guedes") {
    return { densidade: coef.a - coef.b * Math.log10(soma), soma };
  }
  if (!Number.isFinite(idade)) return null;
  return {
    densidade: coef.a - coef.b * soma + coef.c * soma * soma - coef.idade * idade,
    soma,
  };
}

// ── Composição corporal, pelo protocolo que a idade manda ────

// Uma função só para os três protocolos.
//
// Quem chama não escolhe a equação: passa sexo, idade e as dobras, e a
// idade decide. É isso que impede o erro que nenhuma revisão pega depois,
// que é medir um atleta de 19 anos com a equação de criança e comparar o
// resultado com o da avaliação anterior como se fosse a mesma régua.
//
// `protocoloAdulto` só escolhe ENTRE Pollock e Guedes, e não atravessa os
// 18 anos: abaixo disso é Deurenberg e ponto.
export function composicaoCorporal({
  sexo,
  idade,
  peso,
  dobras,
  estagio = null,
  offsetPVC = null,
  conversao = "weststrate",
  protocoloAdulto = "pollock",
}) {
  const protocolo = protocoloDaIdade(idade, protocoloAdulto);
  if (!protocolo) return { completo: false, protocolo: null, faltam: [], percentualGordura: null };

  if (protocolo === "deurenberg") {
    return {
      ...deurenberg({ sexo, idade, peso, dobras, estagio, offsetPVC, conversao }),
      protocolo,
    };
  }

  const sitios = dobrasDoProtocolo(protocolo, sexo);
  const faltam = dobrasFaltantes(dobras, sitios).map(id => `dobras.${id}`);
  if (faltam.length) {
    return { completo: false, protocolo, faltam, soma: null, percentualGordura: null };
  }

  const d = densidadeDeAdulto({ protocolo, sexo, idade, dobras });
  if (!d) return { completo: false, protocolo, faltam: [], soma: null, percentualGordura: null };

  // Siri sempre, nos dois protocolos de adulto: as duas equações foram
  // publicadas com ela, e trocar a conversão desfaz a validação delas.
  const percentual = gorduraDaDensidade(d.densidade, { conversao: "siri" });
  const kg = medida(peso);
  const massaGorda = kg === null || percentual === null ? null : (kg * percentual) / 100;

  return {
    completo: percentual !== null,
    protocolo,
    faltam: [],
    soma: arred(d.soma, 1),
    conversao: "siri",
    densidade: arred(d.densidade, 4),
    percentualGordura: arred(percentual, 2),
    massaGorda: arred(massaGorda, 2),
    massaMagra: kg === null || massaGorda === null ? null : arred(kg - massaGorda, 2),
    classificacao: classificarGordura(percentual, sexo),
  };
}

// ── Somatotipo de Heath-Carter ───────────────────────────────
//
// Três números que descrevem a forma do corpo independentemente do
// tamanho: endomorfia (adiposidade relativa), mesomorfia
// (desenvolvimento músculo-esquelético relativo à estatura) e ectomorfia
// (linearidade). Para um programa de iniciação esportiva ele vale mais
// que o percentual de gordura isolado, porque separa o que mudou de
// gordura do que mudou de massa muscular e de proporção — e é isso que
// distingue um atleta que cresceu de um atleta que treinou.

// A estatura de referência do método.
export const ESTATURA_REFERENCIA = 170.18;

// Endomorfia a partir do somatório de tríceps, subescapular e
// supraespinhal, corrigido pela estatura. A correção existe porque a
// mesma prega de 10 mm significa coisas diferentes num corpo de 1,30 m e
// num de 1,80 m, e o programa compara criança de 7 anos com atleta de 17.
export function endomorfia({ estatura, dobras }) {
  const soma = somaDe(dobras, ["triceps", "subescapular", "supraEspinhal"]);
  const h = medida(estatura);
  if (soma === null || h === null) return null;
  const x = soma * (ESTATURA_REFERENCIA / h);
  return -0.7182 + 0.1451 * x - 0.00068 * x * x + 0.0000014 * x * x * x;
}

// Mesomorfia a partir dos diâmetros ósseos e dos perímetros de braço e
// panturrilha, corrigidos pela dobra do próprio segmento: o perímetro
// medido inclui a gordura subcutânea, e o que a mesomorfia quer é o que
// sobra depois de tirá-la.
//
// `corrigirPerimetros: false` roda a equação com os perímetros crus, e
// existe por um motivo de conferência, não de método.
//
// Reconstruí os dois relatórios que a preparação física usa e eles não
// concordam entre si. Para a Alana em 24/08/2026, o Physical Test 8.0
// imprime mesomorfia 4,70; a equação com os perímetros crus devolve
// 4,7201 e com os perímetros corrigidos devolve 4,4919. Para o Adriano
// em 2024, o informe do avaliador acreditado ISAK imprime 3,34, e é a
// versão corrigida que devolve 3,3359 — a crua devolveria 3,5270.
//
// Ou seja: o Physical Test não desconta a dobra do perímetro, e o
// software ISAK desconta. Heath-Carter pede a correção, então o sistema
// corrige, e as duas endomorfias e as duas ectomorfias dos dois
// relatórios batem na casa dos centésimos — a divergência é só na
// mesomorfia e é só por isso.
//
// O efeito é de cerca de 0,2 unidade nos dois casos, sempre para cima.
// Importa porque é sistemático: uma série que começasse nos números do
// Physical Test e continuasse nos do sistema mostraria o atleta perdendo
// 0,2 de massa relativa no dia da virada, sem ter perdido nada.
// `braco` escolhe qual perímetro entra na equação.
//
// Heath-Carter pede o braço FLETIDO E CONTRAÍDO. O programa mede o
// relaxado, e a decisão da preparação física em 06/10/2026 foi usar o
// relaxado mesmo, porque é o que existe nas 76 coletas de 2026 e é o que
// a equipe vai continuar medindo.
//
// A consequência está medida e não é pequena: no Adriano, relaxado 26,8
// contra fletido 31,4, e os 4,6 cm de diferença valem 0,86 unidade de
// mesomorfia. O somatotipo calculado assim é internamente consistente e
// serve para acompanhar o mesmo atleta ao longo do tempo, que é o uso
// que o programa faz dele. O que ele NÃO serve é para comparar com
// valores de referência da literatura nem com o informe ISAK de 2024,
// porque esses usam o fletido.
//
// Por isso `somatotipo` devolve qual braço usou, e a tela e o PDF
// precisam dizer isso ao lado dos três números. Somatotipo sem a
// convenção do braço escrita junto é um número que parece comparável e
// não é.
export function mesomorfia({
  estatura,
  dobras,
  perimetros,
  diametros,
  corrigirPerimetros = true,
  braco: qualBraco = "contraido",
}) {
  const h = medida(estatura);
  const u = medida(diametros ? diametros.umero : null);
  const f = medida(diametros ? diametros.femur : null);
  const braco = medida(
    perimetros ? (qualBraco === "relaxado" ? perimetros.bracoRelaxado : perimetros.bracoContraido) : null
  );
  const perna = medida(perimetros ? perimetros.panturrilha : null);
  const dobraTriceps = medida(dobras ? dobras.triceps : null);
  const dobraPanturrilha = medida(dobras ? dobras.panturrilha : null);
  if ([h, u, f, braco, perna, dobraTriceps, dobraPanturrilha].some(v => v === null)) return null;

  const bracoCorrigido = corrigirPerimetros ? braco - dobraTriceps / 10 : braco;
  const pernaCorrigida = corrigirPerimetros ? perna - dobraPanturrilha / 10 : perna;
  return (
    0.858 * u + 0.601 * f + 0.188 * bracoCorrigido + 0.161 * pernaCorrigida - 0.131 * h + 4.5
  );
}

// Ectomorfia a partir do índice ponderal (estatura dividida pela raiz
// cúbica da massa). Abaixo de 38,25 o método trava em 0,1: a equação
// linear devolveria valor negativo, e componente negativo não existe.
export function ectomorfia({ estatura, peso }) {
  const h = medida(estatura);
  const kg = medida(peso);
  if (h === null || kg === null) return null;
  const ip = h / Math.cbrt(kg);
  if (ip >= 40.75) return 0.732 * ip - 28.58;
  if (ip > 38.25) return 0.463 * ip - 17.63;
  return 0.1;
}

// As categorias de Carter e Heath. A regra é a dominância: qual
// componente é o maior, e qual dos outros dois é o segundo. "Central" é
// o caso em que nenhum domina — os três ficam dentro de uma unidade.
export function classificarSomatotipo(endo, meso, ecto) {
  if (![endo, meso, ecto].every(Number.isFinite)) return null;
  const maior = Math.max(endo, meso, ecto);
  const menor = Math.min(endo, meso, ecto);
  if (maior - menor <= 1) return "Central";

  const perto = (a, b) => Math.abs(a - b) <= 0.5;
  const C = {
    endo: { v: endo, nome: "endomorfo", adj: "endomórfico" },
    meso: { v: meso, nome: "mesomorfo", adj: "mesomórfico" },
    ecto: { v: ecto, nome: "ectomorfo", adj: "ectomórfico" },
  };
  const ordem = ["endo", "meso", "ecto"].sort((a, b) => C[b].v - C[a].v);
  const dominante = C[ordem[0]];
  const segundo = C[ordem[1]];
  const terceiro = C[ordem[2]];

  let rotulo;
  if (perto(segundo.v, terceiro.v)) rotulo = `${dominante.nome} balanceado`;
  else if (perto(dominante.v, segundo.v)) rotulo = `${segundo.adj}-${dominante.nome}`;
  else rotulo = `${dominante.nome} ${segundo.adj}`;
  return rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
}

// As coordenadas do somatocarta. São elas que permitem desenhar a
// evolução do atleta como um caminho no plano em vez de três linhas
// soltas, e é a leitura que a comissão técnica entende de relance.
export function coordenadasSomatocarta(endo, meso, ecto) {
  if (![endo, meso, ecto].every(Number.isFinite)) return null;
  return { x: arred(ecto - endo), y: arred(2 * meso - (endo + ecto)) };
}

// Distância entre dois somatotipos (SAD, somatotype attitudinal
// distance). É o número que responde "mudou quanto?" entre duas
// avaliações sem precisar olhar os três componentes um a um. Carter
// trata diferença acima de 2 unidades como mudança real de forma.
export function distanciaSomatotipica(a, b) {
  if (!a || !b) return null;
  const d = ["endomorfia", "mesomorfia", "ectomorfia"].map(k => {
    const va = a[k];
    const vb = b[k];
    return Number.isFinite(va) && Number.isFinite(vb) ? vb - va : NaN;
  });
  if (d.some(v => !Number.isFinite(v))) return null;
  return arred(Math.sqrt(d.reduce((s, v) => s + v * v, 0)), 2);
}

// O que ainda falta medir para o somatotipo sair.
//
// A lista cobre dobras, perímetros e diâmetros, e não só as dobras, por
// uma razão prática que apareceu nos dados de 2026: as coletas tinham as
// quatro dobras certas e mesmo assim a mesomorfia não saía, porque
// faltava o braço fletido e faltavam os diâmetros. Uma lista só de
// dobras diria "não falta nada" ao lado de um resultado vazio, e quem
// está com o adipômetro na mão não teria como saber o que buscar.
//
// O caminho vem junto do nome ("perimetros.bracoContraido") porque o
// mesmo rótulo existe em dois lugares: panturrilha é dobra e é perímetro,
// e as duas são necessárias.
export function faltantesDoSomatotipo({
  estatura,
  peso,
  dobras,
  perimetros,
  diametros,
  braco = "contraido",
}) {
  const faltam = dobrasFaltantes(dobras, DOBRAS_SOMATOTIPO).map(id => `dobras.${id}`);
  if (medida(estatura) === null) faltam.push("estatura");
  if (medida(peso) === null) faltam.push("peso");
  const campoDoBraco = braco === "relaxado" ? "bracoRelaxado" : "bracoContraido";
  if (medida(perimetros ? perimetros[campoDoBraco] : null) === null) {
    faltam.push(`perimetros.${campoDoBraco}`);
  }
  if (medida(perimetros ? perimetros.panturrilha : null) === null) {
    faltam.push("perimetros.panturrilha");
  }
  if (medida(diametros ? diametros.umero : null) === null) faltam.push("diametros.umero");
  if (medida(diametros ? diametros.femur : null) === null) faltam.push("diametros.femur");
  return faltam;
}

// Braço relaxado medido e braço fletido ausente.
//
// Vale um aviso próprio porque é o estado real da coleta de 2026 e
// porque o erro é tentador: o relaxado está ali, parece a mesma medida e
// é a que o Physical Test pede por padrão. No Adriano, relaxado 26,8 e
// fletido 31,4. A diferença de 4,6 cm vale 0,86 unidade de mesomorfia,
// quase metade do que Carter trata como mudança de forma corporal — um
// atleta pareceria ter perdido músculo por causa da fita.
export function faltaBracoFletido(perimetros) {
  return (
    medida(perimetros ? perimetros.bracoContraido : null) === null &&
    medida(perimetros ? perimetros.bracoRelaxado : null) !== null
  );
}

export const PERIMETROS_DO_BRACO = ["contraido", "relaxado"];

// Qual braço a série inteira vai usar.
//
// Mesmo problema de `escolherMetodoMaturacao`, e pela mesma razão: o
// perfil ISAK de 2024 tem o braço fletido e as coletas de 2026 só têm o
// relaxado. Usar o fletido em 2024 e o relaxado em 2026 derrubaria a
// mesomorfia do Adriano em 0,86 unidade na virada, sem ele ter perdido
// nada. Então a série usa o braço que existe em TODOS os pontos.
export function escolherPerimetroDoBraco(avaliacoes = []) {
  const validas = avaliacoes.filter(a => a && a.data);
  if (!validas.length) return "relaxado";
  const todasTemFletido = validas.every(
    a => medida(a.perimetros ? a.perimetros.bracoContraido : null) !== null
  );
  return todasTemFletido ? "contraido" : "relaxado";
}

export function somatotipo({ estatura, peso, dobras, perimetros, diametros, braco = "contraido" }) {
  const faltam = faltantesDoSomatotipo({ estatura, peso, dobras, perimetros, diametros, braco });
  const endo = endomorfia({ estatura, dobras });
  const meso = mesomorfia({ estatura, dobras, perimetros, diametros, braco });
  const ecto = ectomorfia({ estatura, peso });
  if (![endo, meso, ecto].every(Number.isFinite)) {
    return {
      completo: false,
      faltam,
      braco,
      bracoRelaxadoNoLugarDoFletido: braco === "contraido" && faltaBracoFletido(perimetros),
      endomorfia: null,
      mesomorfia: null,
      ectomorfia: null,
    };
  }
  const e = arred(endo);
  const m = arred(meso);
  const c = arred(ecto);
  return {
    completo: true,
    faltam: [],
    // A convenção do braço acompanha o resultado para sempre. Sem ela,
    // os três números parecem comparáveis com a literatura e não são.
    braco,
    endomorfia: e,
    mesomorfia: m,
    ectomorfia: c,
    // As coordenadas saem dos valores sem arredondar, e os componentes
    // exibidos saem arredondados. Arredondar antes de calcular joga o
    // erro das casas decimais para dentro do eixo Y, que soma duas vezes
    // a mesomorfia: o ponto no somatocarta não cairia onde o informe do
    // avaliador acreditado o coloca, e a comparação de 2026 com 2024
    // seria contra um ponto deslocado.
    ...coordenadasSomatocarta(endo, meso, ecto),
    classificacao: classificarSomatotipo(e, m, c),
  };
}

// ── Maturação somática ───────────────────────────────────────
//
// Dois atletas de 13 anos podem estar a dois anos de distância um do
// outro em desenvolvimento, e a diferença de desempenho que vem daí não
// é diferença de talento nem de treino. Sem essa medida, o programa
// seleciona os que nasceram mais cedo no ano e os que amadureceram
// antes, que é o erro mais documentado da iniciação esportiva.
//
// O que se estima é a distância em anos até o pico de velocidade de
// crescimento (PVC): negativo é antes do pico, positivo é depois. A
// idade no pico é a idade atual menos essa distância.
//
// Mirwald usa estatura sentado e comprimento de perna. Moore usa só
// idade e estatura. Os dois têm erro padrão perto de meio ano, e isso
// precisa aparecer no relatório: PVC estimado não é data, é faixa.
//
// Hoje o programa afere apenas a estatura em pé, então o método em uso é
// o de Moore. Mirwald continua aqui porque o perfil ISAK de 2024 tem a
// estatura sentado, e porque passar a medi-la custa uma fita e um banco
// — a equação com dois termos a mais erra menos.
export const ERRO_PADRAO_PVC = { mirwald: 0.59, moore: 0.54 };

export function maturacaoMirwald({ sexo, idade, estatura, estaturaSentado, peso }) {
  const h = medida(estatura);
  const hs = medida(estaturaSentado);
  const kg = medida(peso);
  if ([h, hs, kg].some(v => v === null) || !Number.isFinite(idade)) return null;
  const perna = h - hs;
  if (perna <= 0) return null;

  if (sexo === "masculino") {
    return (
      -9.236 +
      0.0002708 * (perna * hs) -
      0.001663 * (idade * perna) +
      0.007216 * (idade * hs) +
      0.02292 * ((kg / h) * 100)
    );
  }
  if (sexo === "feminino") {
    return (
      -9.376 +
      0.0001882 * (perna * hs) +
      0.0022 * (idade * perna) +
      0.005841 * (idade * hs) -
      0.002658 * (idade * kg) +
      0.07693 * ((kg / h) * 100)
    );
  }
  return null;
}

export function maturacaoMoore({ sexo, idade, estatura }) {
  const h = medida(estatura);
  if (h === null || !Number.isFinite(idade)) return null;
  if (sexo === "masculino") return -7.999994 + 0.0036124 * (idade * h);
  if (sexo === "feminino") return -7.709133 + 0.0042232 * (idade * h);
  return null;
}

// O estágio, que é o que a comissão técnica lê. As faixas seguem o uso
// corrente: um ano antes e um ano depois do pico é a janela em que o
// atleta cresce mais rápido, e é nela que a carga de impacto e a
// coordenação pedem atenção.
export function estagioMaturacional(offset) {
  if (!Number.isFinite(offset)) return null;
  if (offset < -1) return "Pré-PVC";
  if (offset <= 1) return "Circa-PVC";
  return "Pós-PVC";
}

// `metodo` fixa qual equação vale. Sem ele, a função usa Mirwald quando
// pode e Moore quando não pode, e é exatamente isso que `compilarSerie`
// não deixa acontecer: no Adriano, Mirwald põe o pico aos 14,5 anos e
// Moore aos 13,9. Uma série que usasse Mirwald em 2024, porque o perfil
// ISAK tem estatura sentado, e Moore em 2026, porque hoje só a estatura
// em pé é medida, mostraria o pico andando sete meses sem que nada
// tivesse acontecido com o atleta. Métodos diferentes não se comparam
// entre si, só consigo mesmos.
export function maturacao({ sexo, idade, estatura, estaturaSentado, peso, metodo: pedido = null }) {
  const mirwald = maturacaoMirwald({ sexo, idade, estatura, estaturaSentado, peso });
  const moore = maturacaoMoore({ sexo, idade, estatura });

  let metodo;
  if (pedido === "mirwald" || pedido === "moore") metodo = pedido;
  else metodo = mirwald !== null ? "mirwald" : "moore";

  const offset = metodo === "mirwald" ? mirwald : moore;
  if (offset === null) return { completo: false, metodo, offset: null };
  return {
    completo: true,
    metodo,
    erroPadrao: ERRO_PADRAO_PVC[metodo],
    offset: arred(offset),
    offsetMirwald: arred(mirwald),
    offsetMoore: arred(moore),
    idadeNoPico: arred(idade - offset),
    estagio: estagioMaturacional(offset),
  };
}

// ── O que o somatotipo quer dizer ────────────────────────────
//
// Três números não dizem nada a quem não é da área, e a carta sozinha
// mostra onde o ponto caiu sem dizer o que isso significa para o atleta.
// Esta função escreve a leitura em uma frase por componente, a partir da
// própria medida.
//
// ── O que ela NÃO faz ───────────────────────────────────────
//
// Não compara com referência de modalidade, não diz se o perfil é bom
// para o esporte que o atleta pratica e não sugere mudança de corpo. O
// somatotipo descreve FORMA, e forma não é qualidade: perfis diferentes
// vencem na mesma prova, e criança muda de forma sozinha enquanto
// cresce. Dizer "precisa ganhar mesomorfia" seria transformar descrição
// em meta, ainda mais num programa de base.
//
// As faixas de leitura são as de Carter: até 2,5 é baixo, de 2,5 a 5 é
// moderado, de 5,5 em diante é alto. Elas descrevem a escala do método,
// e não saúde nem desempenho.
const FAIXAS_DO_COMPONENTE = [
  [2.5, "baixa"],
  [5.0, "moderada"],
  [Infinity, "alta"],
];

const O_QUE_E = {
  endomorfia: {
    nome: "endomorfia",
    curto: "adiposidade relativa",
    baixa: "pouca gordura subcutânea para o tamanho do corpo",
    moderada: "gordura subcutânea dentro do meio da escala",
    alta: "mais gordura subcutânea que a média da escala",
  },
  mesomorfia: {
    nome: "mesomorfia",
    curto: "robustez músculo-esquelética",
    baixa: "estrutura leve, com ossos e músculos finos para a altura",
    moderada: "estrutura músculo-esquelética no meio da escala",
    alta: "estrutura robusta, com ossos largos e músculo volumoso para a altura",
  },
  ectomorfia: {
    nome: "ectomorfia",
    curto: "linearidade",
    baixa: "corpo compacto para o peso que tem",
    moderada: "linearidade no meio da escala",
    alta: "corpo longo e fino para o peso que tem",
  },
};

export function faixaDoComponente(valor) {
  if (!Number.isFinite(valor)) return null;
  return FAIXAS_DO_COMPONENTE.find(([limite]) => valor < limite)[1];
}

// A leitura do somatotipo em texto corrido, pronta para a tela e para o
// papel. Devolve null quando os três componentes não existem.
export function laudoDoSomatotipo(s) {
  if (!s) return null;
  const { endomorfia, mesomorfia, ectomorfia } = s;
  if (![endomorfia, mesomorfia, ectomorfia].every(Number.isFinite)) return null;

  const componentes = [
    { id: "endomorfia", valor: endomorfia },
    { id: "mesomorfia", valor: mesomorfia },
    { id: "ectomorfia", valor: ectomorfia },
  ].sort((a, b) => b.valor - a.valor);

  const [primeiro, segundo] = componentes;
  const empateNoTopo = primeiro.valor - segundo.valor < 0.5;
  const classificacao = s.classificacao || classificarSomatotipo(endomorfia, mesomorfia, ectomorfia);

  // A frase de abertura segue o mesmo critério da classificação: quando
  // os dois primeiros estão a menos de meia unidade, nenhum predomina.
  const abertura = empateNoTopo
    ? `Os componentes se equilibram, com ${O_QUE_E[primeiro.id].nome} e `
      + `${O_QUE_E[segundo.id].nome} praticamente no mesmo nível`
    : `Predomina a ${O_QUE_E[primeiro.id].nome}, seguida da ${O_QUE_E[segundo.id].nome}`;

  const leitura = componentes.map(c => {
    const faixa = faixaDoComponente(c.valor);
    return `${O_QUE_E[c.id].nome} ${c.valor.toString().replace(".", ",")} (${faixa}), `
      + `${O_QUE_E[c.id][faixa]}`;
  }).join("; ");

  return {
    classificacao,
    dominante: primeiro.id,
    texto:
      `${abertura}. Na escala de Heath-Carter: ${leitura}.`,
    // A ressalva anda junto do laudo em todo lugar onde ele aparece. Sem
    // ela, "ectomorfo" vira diagnóstico e o próximo passo vira meta de
    // corpo, que é o que um programa de base não deve fazer.
    //
    // O texto é este, e inteiro: o Diego fixou a frase em 15/10/2026.
    ressalva: "O somatotipo descreve a forma do corpo, não a qualidade do atleta.",
  };
}

// ── O que a maturação quer dizer ─────────────────────────────
//
// O estágio sai de uma estimativa do pico de velocidade de crescimento,
// e o texto diz o que ele significa para quem vai treinar o atleta, que
// é a única razão de o número estar na ficha.
export function laudoDaMaturacao(m) {
  if (!m || !m.completo) return null;
  const anosAtePico = Number.isFinite(m.offset) ? -m.offset : null;

  let fase;
  if (m.estagio === "Pré-PVC") {
    fase = anosAtePico !== null && anosAtePico > 0
      ? `Está ANTES do pico de crescimento, a cerca de ${anosAtePico.toFixed(1).replace(".", ",")} `
        + "ano(s) dele"
      : "Está antes do pico de crescimento";
  } else if (m.estagio === "Circa-PVC") {
    fase = "Está NA JANELA do pico de crescimento, o período de um ano para cada lado do pico, "
      + "quando o corpo muda mais rápido";
  } else {
    fase = "Já PASSOU do pico de crescimento há mais de um ano";
  }

  return {
    estagio: m.estagio,
    texto:
      `${fase}. O pico estimado é aos ${String(m.idadeNoPico).replace(".", ",")} anos, por `
      + `${m.metodo === "mirwald" ? "Mirwald" : "Moore"}, com erro padrão de cerca de `
      + `${String(m.erroPadrao).replace(".", ",")} ano.`,
    ressalva: "É estimativa feita a partir de idade, estatura e peso, e não exame.",
  };
}

// ── Fracionamento da massa corporal ──────────────────────────
//
// O percentual de gordura divide o corpo em dois: gordura e o resto. O
// fracionamento divide em quatro, e é isso que distingue um atleta que
// ganhou músculo de um que só ganhou peso. É o que o informe do Physical
// Test imprime como peso gordo, peso ósseo, peso residual e peso
// muscular, e é o que a comissão técnica usa para decidir carga.
//
// O modelo é o clássico de quatro compartimentos:
//
//   massa gorda     ← o protocolo de dobras, já calculado
//   massa óssea     ← Von Döbeln (1964), adaptada por Rocha (1975)
//   massa residual  ← Würch (1974), percentual fixo do peso por sexo
//   massa muscular  ← o que sobra: peso − (gorda + óssea + residual)
//
// A massa muscular é subtração e não equação, e isso tem consequência:
// ela acumula o erro dos outros três. Um percentual de gordura 2 pontos
// alto vira 2 pontos de músculo a menos, sem que nada no número avise.
// Por isso a tela mostra os quatro juntos e nunca a massa muscular
// isolada.
//
// Rocha M (1975). Peso ósseo do brasileiro de ambos os sexos de 17 a 25
//   anos. Arquivos de Anatomia e Antropologia 1:445-51.
// Würch A (1974). La femme et le sport. Médecine Sportive Française
//   4(1):441-5.

// O percentual do peso que é massa residual: vísceras, órgãos e o que
// não é gordura, osso nem músculo.
//
// É percentual fixo por sexo, e isso é limite do método e não escolha
// nossa: Würch não propôs equação, propôs proporção. O informe do
// Physical Test usa exatamente estes dois números — no Adrian Dastan,
// 52,80 kg vezes 24,1% dá 12,72 kg, que é o que ele imprime.
export const PERCENTUAL_RESIDUAL = { masculino: 0.241, feminino: 0.209 };

// A faixa de idade em que Rocha derivou o peso ósseo.
export const FAIXA_ROCHA = { de: 17, ate: 26 };

// Massa óssea em kg.
//
// Entram a estatura, o diâmetro biestiloide (punho, rádio-ulnar) e o
// biepicôndilo femoral. Os três em metros dentro da conta, porque a
// equação foi publicada assim, e em centímetros na coleta, porque é como
// o paquímetro lê.
export function massaOsseaRocha({ estatura, diametros }) {
  const h = medida(estatura);
  const punho = medida(diametros ? diametros.biestiloide : null);
  const femur = medida(diametros ? diametros.femur : null);
  if (h === null || punho === null || femur === null) return null;
  const est = h / 100;
  return 3.02 * Math.pow(est * est * (punho / 100) * (femur / 100) * 400, 0.712);
}

// As quatro massas, ou o que falta medir para elas existirem.
//
// `percentualGordura` vem de fora, do protocolo que a idade ou o
// avaliador escolheu, para o fracionamento não ter uma segunda opinião
// sobre a gordura do mesmo atleta.
export function fracionamento({
  sexo,
  idade,
  peso,
  estatura,
  diametros,
  percentualGordura,
}) {
  const kg = medida(peso);
  const faltam = [];
  if (kg === null) faltam.push("peso");
  if (medida(estatura) === null) faltam.push("estatura");
  if (medida(diametros ? diametros.biestiloide : null) === null) {
    faltam.push("diametros.biestiloide");
  }
  if (medida(diametros ? diametros.femur : null) === null) faltam.push("diametros.femur");
  if (!Number.isFinite(percentualGordura)) faltam.push("percentualGordura");

  const residualPorSexo = PERCENTUAL_RESIDUAL[sexo];
  if (!residualPorSexo) faltam.push("sexo");

  if (faltam.length) {
    return {
      completo: false,
      faltam,
      metodos: { ossea: "rocha", residual: "wurch" },
      massaGorda: null,
      massaOssea: null,
      massaResidual: null,
      massaMuscular: null,
    };
  }

  const massaGorda = (kg * percentualGordura) / 100;
  const massaOssea = massaOsseaRocha({ estatura, diametros });
  const massaResidual = kg * residualPorSexo;
  const massaMuscular = kg - (massaGorda + massaOssea + massaResidual);

  const pct = v => arred((v / kg) * 100, 1);

  return {
    completo: massaMuscular > 0,
    faltam: [],
    metodos: { ossea: "rocha", residual: "wurch" },
    // Músculo negativo não é resultado, é erro de medida em algum dos
    // outros três, e passar isso adiante como "0 kg de músculo" seria
    // esconder justamente o que precisa ser conferido.
    massaMuscularNegativa: massaMuscular <= 0,
    massaGorda: arred(massaGorda, 2),
    massaOssea: arred(massaOssea, 2),
    massaResidual: arred(massaResidual, 2),
    massaMuscular: arred(massaMuscular, 2),
    percentualGordura: arred(percentualGordura, 1),
    percentualOsseo: pct(massaOssea),
    percentualResidual: pct(massaResidual),
    percentualMuscular: pct(massaMuscular),
    // Rocha mediu brasileiros de 17 a 25 anos. A maioria do programa é
    // mais nova que isso, e o aviso acompanha o número em vez de ficar
    // num rodapé: osso de criança não é osso de adulto em escala.
    ressalva:
      Number.isFinite(idade) && idade < FAIXA_ROCHA.de
        ? "A massa óssea sai pela equação de Rocha, derivada em brasileiros de "
          + `${FAIXA_ROCHA.de} a ${FAIXA_ROCHA.ate - 1} anos. Com `
          + `${idade.toFixed(1).replace(".", ",")} anos o atleta está abaixo dessa faixa, `
          + "e o valor serve para acompanhar a própria evolução, não para comparar com referência."
        : null,
  };
}

// ── Índices de saúde ─────────────────────────────────────────
//
// Índices que saem de peso, estatura e dois perímetros, e que respondem
// a uma pergunta que a gordura total não responde: ONDE está a gordura.
// Gordura abdominal e gordura periférica têm risco diferente com o mesmo
// percentual.
//
// ── A ressalva que precisa estar na tela ────────────────────
//
// Os pontos de corte são de ADULTO e foram derivados para risco
// cardiometabólico em população geral. O programa atende atletas de 9 a
// 30 anos, a maioria criança: aplicar o corte de adulto numa criança
// produz classificação sem validade, e aplicar em atleta produz
// classificação sem sentido, porque a referência não é de atleta.
//
// Por isso o módulo devolve o VALOR sempre e a classificação só quando
// ela vale, com o aviso ao lado. Número sem classificação serve para
// acompanhar o próprio atleta no tempo, que é o uso legítimo aqui.
//
// Valdez R (1991). A simple model-based index of abdominal adiposity.
//   J Clin Epidemiol 44:955-6.
// Ashwell M, Hsieh SD (2005). Six reasons why the waist-to-height ratio
//   is a rapid and effective global indicator. Int J Food Sci Nutr 56:303-7.

// A constante geométrica do índice de conicidade. Vem da raiz de 4 pi
// sobre a densidade média do corpo humano, 1050 kg/m³, e não é um
// coeficiente ajustado: é o cilindro de referência do modelo.
export const CONSTANTE_CONICIDADE = 0.109;

// O índice de conicidade compara a cintura medida com a cintura de um
// cilindro de mesmo peso e mesma estatura. 1,00 é o cilindro perfeito e
// 1,73 é o limite geométrico do cone duplo.
export function indiceDeConicidade({ peso, estatura, perimetros }) {
  const kg = medida(peso);
  const h = medida(estatura);
  const c = medida(perimetros ? perimetros.cintura : null);
  if (kg === null || h === null || c === null) return null;
  const est = h / 100;
  return c / 100 / (CONSTANTE_CONICIDADE * Math.sqrt(kg / est));
}

const FAIXAS_RCQ = {
  masculino: [[0.95, "Baixo"], [1.0, "Moderado"], [Infinity, "Alto"]],
  feminino: [[0.8, "Baixo"], [0.85, "Moderado"], [Infinity, "Alto"]],
};

export const LIMITE_CINTURA_ESTATURA = 0.5;

export function indicesDeSaude({
  sexo,
  idade,
  peso,
  estatura,
  estaturaSentado,
  envergadura,
  perimetros,
  dobras,
}) {
  const kg = medida(peso);
  const h = medida(estatura);
  const cintura = medida(perimetros ? perimetros.cintura : null);
  const quadril = medida(perimetros ? perimetros.quadril : null);
  const sentado = medida(estaturaSentado);
  const braco = medida(envergadura);

  const imc = kg === null || h === null ? null : kg / Math.pow(h / 100, 2);
  const rcq = cintura === null || quadril === null ? null : cintura / quadril;
  const rcEst = cintura === null || h === null ? null : cintura / h;
  const conicidade = indiceDeConicidade({ peso, estatura, perimetros });

  // A classificação do IMC em criança e adolescente depende de curva de
  // referência por idade e sexo, que o sistema não carrega. Classificar
  // pela faixa de adulto um atleta de 11 anos daria "abaixo do peso"
  // para metade do núcleo, e é por isso que aqui não sai rótulo nenhum
  // abaixo dos 18.
  const adulto = Number.isFinite(idade) && idade >= IDADE_ADULTA;
  const faixas = FAIXAS_RCQ[sexo];

  return {
    imc: arred(imc),
    // Somatório das dobras medidas, em milímetros. É a variável mais
    // direta que a antropometria tem: ela é medida, e o percentual de
    // gordura é estimativa feita em cima dela.
    somaDasDobras: arred(somaDoQueFoiMedido(dobras, DOBRAS.map(d => d.id)), 1),
    sitiosMedidos: DOBRAS.filter(d => medida(dobras ? dobras[d.id] : null) !== null).length,
    razaoCinturaQuadril: arred(rcq, 2),
    razaoCinturaEstatura: arred(rcEst, 2),
    indiceDeConicidade: arred(conicidade),
    classificacaoRCQ: adulto && rcq !== null && faixas
      ? faixas.find(([limite]) => rcq < limite)[1]
      : null,
    // A cintura-estatura é a única que se usa igual em criança e em
    // adulto, com o mesmo 0,5, e é por isso que ela entra aqui mesmo
    // sendo a mais simples das quatro.
    cinturaAcimaDaMetadeDaEstatura: rcEst === null ? null : rcEst >= LIMITE_CINTURA_ESTATURA,

    // ── Proporções ───────────────────────────────────────────
    //
    // As duas são razão entre medidas do próprio atleta, sem equação de
    // população no meio, e por isso saem sem classificação: o que elas
    // respondem é como ESTE corpo está mudando de proporção.
    //
    // Índice córmico é a estatura sentado sobre a estatura, em
    // percentual: quanto do corpo é tronco. Ele cai no estirão, porque a
    // perna cresce antes do tronco, e volta a subir no fim. É a leitura
    // de crescimento que o peso e a estatura sozinhos não dão.
    //
    // A envergadura sobre a estatura fica perto de 1 e passa disso em
    // quem tem braço longo para a altura. Muda durante o estirão pelo
    // mesmo motivo do córmico, e em BMX, skate e tênis de mesa ela
    // interessa por alcance.
    indiceCormico: sentado === null || h === null ? null : arred((sentado / h) * 100, 1),
    razaoEnvergaduraEstatura: braco === null || h === null ? null : arred(braco / h, 3),
    // Verdadeiro quando o atleta ainda não é adulto e, portanto, os
    // únicos pontos de corte que existem para estes índices não valem
    // para ele. A tela mostra os valores e diz isso ao lado.
    avisarReferenciaDeAdulto: !adulto,
  };
}

// Circunferência muscular do braço, em cm.
//
// Desconta a gordura subcutânea do perímetro: o braço de 24 cm de quem
// tem 20 mm de dobra tricipital tem menos músculo que o braço de 24 cm
// de quem tem 6. É a medida que faz o gráfico de distribuição muscular
// dizer algo sobre músculo em vez de sobre tamanho.
//
// Frisancho AR (1981). New norms of upper limb fat and muscle areas for
//   assessment of nutritional status. Am J Clin Nutr 34:2540-5.
export function circunferenciaMuscular(perimetro, dobraEmMm) {
  const p = medida(perimetro);
  const d = medida(dobraEmMm);
  if (p === null || d === null) return null;
  return arred(p - (Math.PI * d) / 10, 1);
}

// O ponto médio da somatocarta: a média dos três componentes.
//
// Serve a duas leituras. Na série de um atleta, é o centro da nuvem dele,
// e a distância de cada ponto ao centro mostra se ele está mudando de
// forma ou oscilando em volta da mesma. Num grupo, é o somatotipo médio
// da modalidade, que é como Carter descreve perfis esportivos.
export function somatotipoMedio(somatotipos = []) {
  const validos = somatotipos.filter(
    s => s && ["endomorfia", "mesomorfia", "ectomorfia"].every(k => Number.isFinite(s[k])),
  );
  if (!validos.length) return null;
  const media = k => validos.reduce((soma, s) => soma + s[k], 0) / validos.length;
  const endo = arred(media("endomorfia"));
  const meso = arred(media("mesomorfia"));
  const ecto = arred(media("ectomorfia"));
  return {
    n: validos.length,
    endomorfia: endo,
    mesomorfia: meso,
    ectomorfia: ecto,
    classificacao: classificarSomatotipo(endo, meso, ecto),
    ...coordenadasSomatocarta(endo, meso, ecto),
  };
}

// ── Compilação ───────────────────────────────────────────────

// Uma avaliação compilada: tudo que se calcula a partir de uma coleta.
//
// `sexo` vem do cadastro do atleta e não da coleta. Todas as equações
// deste arquivo são específicas por sexo, e as duas de maturação chegam a
// trocar o sinal de um termo entre elas — rodar a equação masculina numa
// atleta não devolve um resultado aproximado, devolve um resultado sem
// sentido. Por isso a função devolve `faltaSexo` em vez de assumir um
// padrão, e a tela precisa cobrar o campo antes de aceitar a coleta.
export function compilarAvaliacao({
  sexo,
  dataNascimento,
  medidas,
  conversao = "weststrate",
  metodoMaturacao = null,
  bracoDoSomatotipo = "contraido",
  protocoloAdulto = "pollock",
}) {
  const m = medidas || {};
  const data = m.data || null;
  const idade = dataNascimento ? idadeDecimalEm(dataNascimento, data) : null;
  const base = {
    data,
    origem: m.origem || "propria",
    idade: arred(idade, 2),
    faltaSexo: sexo !== "masculino" && sexo !== "feminino",
    faltaNascimento: !Number.isFinite(idade),
    peso: medida(m.peso),
    estatura: medida(m.estatura),
    estaturaSentado: medida(m.estaturaSentado),
    // O diâmetro do punho desta coleta não foi medido: foi reconstruído
    // invertendo a equação de Rocha sobre o peso ósseo que o Physical
    // Test imprimiu. O resultado do fracionamento é o mesmo, e quem lê
    // tem de saber a diferença, então a marca acompanha o ponto.
    biestiloideReconstruido: Boolean(m.biestiloideReconstruido),
    // O campo continua se chamando `indices` porque a ficha, o PDF e o
    // gráfico de evolução leem por esse nome. O conteúdo cresceu: IMC,
    // cintura-quadril, cintura-estatura, conicidade e o somatório das
    // dobras medidas.
    indices: indicesDeSaude({
      sexo,
      idade,
      peso: m.peso,
      estatura: m.estatura,
      estaturaSentado: m.estaturaSentado,
      envergadura: m.envergadura,
      perimetros: m.perimetros,
      dobras: m.dobras,
    }),
  };
  if (base.faltaSexo || base.faltaNascimento) {
    return { ...base, deurenberg: null, fracionamento: null, somatotipo: null, maturacao: null };
  }

  // O protocolo que o avaliador escolheu na hora de medir, se ele serve.
  //
  // A escolha é gravada junto da medida e vale só para aquela data. Ela
  // existe por um motivo estreito: dos 18 em diante, Pollock e Guedes são
  // os dois válidos e a comissão escolhe. Escolha inadequada não muda
  // conta nenhuma — a idade decide, como antes — e o ponto compilado sai
  // com `protocoloIgnorado` para a tela poder dizer que a escolha gravada
  // não foi usada, em vez de mostrar um número que não combina com ela.
  const pedido = m.protocoloGordura || null;
  const serve = pedido
    ? adequacaoDoProtocolo({ protocolo: pedido, sexo, idade }).adequado === true
    : false;
  const adulto = serve && pedido !== "deurenberg" ? pedido : protocoloAdulto;

  // A maturação sai primeiro porque o estágio de Deurenberg é sugerido a
  // partir dela: o pico de velocidade de crescimento é o que o programa
  // tem no lugar do estágio de Tanner.
  const mat = maturacao({
    sexo,
    idade,
    estatura: m.estatura,
    estaturaSentado: m.estaturaSentado,
    peso: m.peso,
    metodo: metodoMaturacao,
  });

  const composicao = composicaoCorporal({
    sexo,
    idade,
    peso: m.peso,
    dobras: m.dobras,
    // `maturacao` é o nome antigo do campo, de quando ele guardava duas
    // fases. Os dois são lidos porque as 76 coletas de 2026 já estão
    // gravadas, e uma delas tem o estágio vindo do Physical Test.
    estagio: m.estagioMaturacional || m.maturacao || null,
    offsetPVC: mat && mat.completo ? mat.offset : null,
    conversao,
    protocoloAdulto: adulto,
  });

  return {
    ...base,
    protocoloPedido: pedido,
    protocoloIgnorado: Boolean(pedido) && !serve,
    // `deurenberg` é o nome do campo por história, e agora ele carrega o
    // resultado do protocolo que a IDADE manda: Deurenberg até os 17,
    // Pollock ou Guedes dos 18 em diante. O objeto diz qual foi em
    // `protocolo`, e a tela mostra o nome ao lado do número.
    //
    // O nome do campo fica como está de propósito: renomear quebraria a
    // ficha, o PDF e o Excel de uma vez, e o que importa é o resultado
    // trazer o protocolo junto, não a chave se chamar bonito.
    deurenberg: composicao,
    // O fracionamento recebe a gordura já calculada, e não as dobras: o
    // corpo não pode ter dois percentuais de gordura na mesma data, um
    // para o gráfico de linha e outro para o de pizza.
    fracionamento: fracionamento({
      sexo,
      idade,
      peso: m.peso,
      estatura: m.estatura,
      diametros: m.diametros,
      percentualGordura: composicao.percentualGordura,
    }),
    somatotipo: somatotipo({ ...m, braco: bracoDoSomatotipo }),
    maturacao: mat,
  };
}

// A série do atleta: cada avaliação compilada, em ordem de data, com o
// que mudou desde a anterior.
//
// A velocidade de crescimento sai daqui e não da avaliação isolada
// porque ela só existe entre dois pontos. É ela, e não a estatura, que
// diz se o atleta está dentro do estirão: 8 cm/ano num menino de 13 anos
// é outra conversa sobre carga de treino do que 2 cm/ano.
//
// Intervalos curtos são marcados como `intervaloCurto`: dois meses entre
// coletas transformam meio centímetro de erro de medida numa velocidade
// de 3 cm/ano que nunca existiu.
export const INTERVALO_MINIMO_VELOCIDADE = 0.5;


// O método de maturação é escolhido uma vez para a série inteira, e é o
// mais pobre dos que dão para rodar em todos os pontos: Mirwald só se
// TODA avaliação tiver estatura sentado, senão Moore em todas.
//
// Isso parece desperdício no caso real — o perfil ISAK de 2024 tem
// estatura sentado e as coletas de 2026 não têm — e é o contrário. Usar
// a equação melhor em 2024 e a pior em 2026 faria o pico estimado andar
// sete meses entre as duas datas por troca de equação, e é justamente
// esse deslocamento que o relatório interpretaria como maturação.
export function escolherMetodoMaturacao(avaliacoes = []) {
  const validas = avaliacoes.filter(a => a && a.data);
  if (!validas.length) return "moore";
  return validas.every(a => medida(a.estaturaSentado) !== null) ? "mirwald" : "moore";
}

export function compilarSerie({
  sexo,
  dataNascimento,
  avaliacoes = [],
  conversao = "weststrate",
  protocoloAdulto = "pollock",
}) {
  const ordenadas = [...avaliacoes]
    .filter(a => a && a.data)
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));
  const metodoMaturacao = escolherMetodoMaturacao(ordenadas);
  const bracoDoSomatotipo = escolherPerimetroDoBraco(ordenadas);

  let anterior = null;
  let primeira = null;
  return ordenadas.map(medidas => {
    const atual = compilarAvaliacao({
      sexo,
      dataNascimento,
      medidas,
      conversao,
      metodoMaturacao,
      bracoDoSomatotipo,
      protocoloAdulto,
    });
    const delta = anterior ? variacao(anterior, atual) : null;
    // O acumulado desde a primeira avaliação, ao lado do intervalo.
    //
    // Os dois respondem perguntas diferentes e uma não substitui a
    // outra. O intervalo diz o que mudou desde a última vez, que é o que
    // julga o bloco de treino que acabou. O acumulado diz para onde o
    // atleta foi desde que entrou, e é nele que aparece o que a soma dos
    // intervalos esconde: três quedas de meio ponto de gordura seguidas
    // são um ponto e meio, e nenhuma das três parecia muito.
    const desdeAPrimeira = primeira ? variacao(primeira, atual) : null;
    anterior = atual;
    if (!primeira) primeira = atual;
    return { ...atual, delta, desdeAPrimeira, dataDaPrimeira: primeira.data };
  });
}

function variacao(de, para) {
  const anos =
    Number.isFinite(de.idade) && Number.isFinite(para.idade) ? para.idade - de.idade : null;
  const dif = (a, b) => (Number.isFinite(a) && Number.isFinite(b) ? arred(b - a) : null);
  const cresceu = dif(de.estatura, para.estatura);
  return {
    anos: arred(anos),
    intervaloCurto: anos !== null && anos < INTERVALO_MINIMO_VELOCIDADE,
    estatura: cresceu,
    // Criança em idade de programa não encurta. Quando a estatura cai
    // entre duas coletas, uma das duas medidas está errada, e não há
    // como saber qual olhando só para as duas: na Alana, entre janeiro
    // (131,5) e março (130,0) a conta dá −7,1 cm/ano, e a estatura que a
    // equipe considera boa é a de agosto. A marca existe para o
    // relatório não publicar crescimento negativo como se fosse dado.
    estaturaRegrediu: cresceu !== null && cresceu < 0,
    velocidadeDeCrescimento: cresceu !== null && anos ? arred(cresceu / anos, 1) : null,
    peso: dif(de.peso, para.peso),
    percentualGordura: dif(
      de.deurenberg ? de.deurenberg.percentualGordura : null,
      para.deurenberg ? para.deurenberg.percentualGordura : null
    ),
    massaMagra: dif(
      de.deurenberg ? de.deurenberg.massaMagra : null,
      para.deurenberg ? para.deurenberg.massaMagra : null
    ),
    somatotipo: distanciaSomatotipica(de.somatotipo, para.somatotipo),
  };
}

// ── Entrada: perfil ISAK ─────────────────────────────────────

// Traduz um perfil ISAK para o formato de medidas deste arquivo.
//
// Os nomes do lado esquerdo são os que saem no informe do avaliador
// acreditado. A tradução é só renomeação: nenhum valor é convertido,
// porque os dois lados estão nas mesmas unidades (dobras em mm,
// perímetros e diâmetros em cm). O que muda é que, depois disto, a
// avaliação de 2024 passa pelas mesmas equações das de 2026.
//
// A correspondência que importa conferir é "Crista ilíaca" → supra-
// ilíaca: são o mesmo ponto anatômico com dois nomes, e é a dobra que
// Deurenberg pede. "Supraspinal" é outro ponto e vai para a
// supraespinhal, que é a que o somatotipo pede. Trocar as duas troca os
// dois protocolos ao mesmo tempo, e nenhum dos dois resultados fica
// obviamente errado na tela.
export function medidasDeISAK(perfil) {
  if (!perfil) return null;
  const pegar = (lista, origem) =>
    Object.fromEntries(lista.map(({ id, isak }) => [id, medida(origem ? origem[isak] : null)]));
  return {
    data: perfil.data || null,
    origem: "isak",
    peso: medida(perfil.massaCorporal !== undefined ? perfil.massaCorporal : perfil.peso),
    estatura: medida(perfil.estatura),
    estaturaSentado: medida(perfil.estaturaSentado),
    dobras: pegar(DOBRAS, perfil.dobras),
    perimetros: pegar(PERIMETROS, perfil.perimetros),
    diametros: pegar(DIAMETROS, perfil.diametros),
  };
}
